import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useAuth } from "@/lib/auth";
import { ClientShell } from "@/components/ClientShell";
import { AdminShell } from "@/components/AdminShell";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { CookieConsent } from "@/components/CookieConsent";

import Cover from "@/pages/onboarding/Cover";
import IntroOnboarding from "@/pages/onboarding/IntroOnboarding";
import ChooseJourney from "@/pages/onboarding/ChooseJourney";
import EnrollRegenMethod from "@/pages/onboarding/EnrollRegenMethod";
import TrackerOnboarding from "@/pages/onboarding/TrackerOnboarding";
import Method from "@/pages/onboarding/Method";
import Receive from "@/pages/onboarding/Receive";
import Preview from "@/pages/onboarding/Preview";
import FlexibleNutrition from "@/pages/onboarding/FlexibleNutrition";
import Program from "@/pages/onboarding/Program";
import Questionnaire from "@/pages/onboarding/Questionnaire";
import Register from "@/pages/auth/Register";
import OnboardFlow from "@/pages/onboarding/OnboardFlow";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Application from "@/pages/auth/Application";
import Checkout from "@/pages/auth/Checkout";

import Home from "@/pages/client/Home";
import CalendarPage from "@/pages/client/Calendar";
import Training from "@/pages/client/Training";
import Nutrition from "@/pages/client/Nutrition";
import Recovery from "@/pages/client/Recovery";
import SleepLog from "@/pages/client/SleepLog";
import FastingLog from "@/pages/client/FastingLog";
import RecoveryInsights from "@/pages/client/RecoveryInsights";
import CoachAnalysis from "@/pages/client/CoachAnalysis";
import Mindset from "@/pages/client/Mindset";
import StepsLog from "@/pages/client/StepsLog";
import ScoreBreakdown from "@/pages/client/ScoreBreakdown";
import Progress from "@/pages/client/Progress";
import Settings from "@/pages/client/Settings";
import CoachChat from "@/pages/client/CoachChat";
import { TierGate } from "@/components/UpgradeWall";
import WeeklyDigestPage from "@/pages/client/WeeklyDigest";
import Academy from "@/pages/client/Academy";
import PaymentReturn from "@/pages/client/PaymentReturn";
import LegalDoc from "@/pages/legal/LegalDoc";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminClients from "@/pages/admin/Clients";
import AdminClientDetail from "@/pages/admin/ClientDetail";
import AdminSubscriptions from "@/pages/admin/Subscriptions";
import AdminTraining from "@/pages/admin/Training";
import AdminNutrition from "@/pages/admin/Nutrition";
import AdminRecovery from "@/pages/admin/Recovery";
import AdminAI from "@/pages/admin/AI";
import AdminMessages from "@/pages/admin/Messages";
import AdminBroadcasts from "@/pages/admin/Broadcasts";
import AdminPayments from "@/pages/admin/Payments";
import AdminReferrals from "@/pages/admin/Referrals";
import AdminAccessCodes from "@/pages/admin/AccessCodes";
import AdminTips from "@/pages/admin/Tips";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminScoring from "@/pages/admin/Scoring";
import AdminPrivacy from "@/pages/admin/Privacy";
import AdminCountryPricing from "@/pages/admin/CountryPricing";
import AdminLandingPricing from "@/pages/admin/LandingPricing";
import AdminFoods from "@/pages/admin/Foods";
import AdminActivities from "@/pages/admin/Activities";
import AdminAcademy from "@/pages/admin/Academy";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Onboarding */}
            <Route path="/" element={<LandingOrRedirect />} />
            <Route path="/payments/return" element={<PaymentReturn />} />
            <Route path="/onboarding/method" element={<Method />} />
            <Route path="/onboarding/receive" element={<Receive />} />
            <Route path="/onboarding/nutrition" element={<FlexibleNutrition />} />
            <Route path="/onboarding/preview" element={<Preview />} />
            <Route path="/onboarding/program" element={<Program />} />

            {/* Auth */}
            <Route path="/register" element={<OnboardFlow />} />
            <Route path="/login" element={<Login />} />
            <Route path="/intro" element={<IntroOnboarding />} />
            <Route path="/choose-journey" element={<ProtectedRoute role="client"><ChooseJourney /></ProtectedRoute>} />
            <Route path="/enroll/regen-method" element={<ProtectedRoute role="client"><EnrollRegenMethod /></ProtectedRoute>} />
            <Route path="/tracker-onboarding" element={<ProtectedRoute role="client"><TrackerOnboarding /></ProtectedRoute>} />
            <Route path="/legal/:slug" element={<LegalDoc />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/application" element={<ProtectedRoute><Application /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="/onboarding/questionnaire" element={<ProtectedRoute role="client"><Questionnaire /></ProtectedRoute>} />

            {/* Client app */}
            <Route path="/app" element={<ProtectedRoute role="client"><ClientShell /></ProtectedRoute>}>
              <Route index element={<Navigate to="/app/home" replace />} />
              <Route path="home" element={<Home />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="training" element={<Training />} />
              <Route path="nutrition" element={<Nutrition />} />
              <Route path="recovery" element={<Recovery />} />
              <Route path="sleep" element={<SleepLog />} />
              <Route path="fasting" element={<FastingLog />} />
              <Route path="insights" element={<RecoveryInsights />} />
              <Route path="coach-analysis" element={<TierGate needed="regen_method" feature="Coach JP Analysis"><CoachAnalysis /></TierGate>} />
              <Route path="mindset" element={<Mindset />} />
              <Route path="steps" element={<StepsLog />} />
              <Route path="score" element={<TierGate needed="regen_plus" feature="YourReGen Score"><ScoreBreakdown /></TierGate>} />
              <Route path="progress" element={<Progress />} />
              <Route path="digest" element={<WeeklyDigestPage />} />
              <Route path="academy" element={<Academy />} />
              <Route path="settings" element={<Settings />} />
              <Route path="coach" element={<TierGate needed="regen_method" feature="Coach JP"><CoachChat /></TierGate>} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminShell /></ProtectedRoute>}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:id" element={<AdminClientDetail />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="training" element={<AdminTraining />} />
              <Route path="activities" element={<AdminActivities />} />
              <Route path="nutrition" element={<AdminNutrition />} />
              <Route path="foods" element={<AdminFoods />} />
              <Route path="recovery" element={<AdminRecovery />} />
              <Route path="ai" element={<AdminAI />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="broadcasts" element={<AdminBroadcasts />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="referrals" element={<AdminReferrals />} />
              <Route path="access-codes" element={<AdminAccessCodes />} />
              <Route path="tips" element={<AdminTips />} />
              <Route path="academy" element={<AdminAcademy />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="scoring" element={<AdminScoring />} />
              <Route path="privacy" element={<AdminPrivacy />} />
              <Route path="pricing" element={<AdminCountryPricing />} />
              <Route path="website-pricing" element={<AdminLandingPricing />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster theme="dark" position="top-center" />
          <PWAInstallPrompt />
          <CookieConsent />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Public root: authenticated users go to their dashboard; everyone else sees the
// approved blue-and-white landing page (served as a static file at /concept.html).
function LandingOrRedirect() {
  const { user, loading } = useAuth();
  // Visitors see the public homepage at the root URL itself (no /concept.html in the address bar).
  useEffect(() => {
    if (loading || user) return;
    fetch('/concept.html', { cache: 'no-cache' })
      .then(r => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then(html => { document.open(); document.write(html); document.close(); })
      .catch(() => window.location.replace('/concept.html'));
  }, [loading, user]);
  if (!loading && user) return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/app/home'} replace />;
  return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
}

export default App;